import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import "./InicioGeneral.css";

const CACHE_KEY = "bomberos_en_servicio_cuerpos_activos_v1";

function numeroOrden(valor) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 999999;
}

function cuerpoSerializable(docSnap) {
  const d = docSnap.data();
  return {
    id: docSnap.id,
    nombre: d.nombre || "",
    slug: d.slug || "",
    activo: d.activo !== false,
    comuna: d.comuna || "",
    region: d.region || "",
    orden: d.orden ?? 999999,
    logoURL: d.logoURL || d.logo_url || d.logoUrl || d.logo || "",
  };
}

function leerCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function guardarCache(lista) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(lista));
  } catch (_) {}
}

function logoCuerpo(data) {
  const remoto = String(
    data?.logoURL || data?.logo_url || data?.logoUrl || data?.logo || ""
  ).trim();
  if (remoto) return remoto;

  // Compatibilidad con Huasco mientras el logo histórico siga en /public/logos.
  if (String(data?.slug || data?.id || "").toLowerCase() === "huasco") {
    return "/logos/logo-cuerpo.png";
  }

  return "";
}

export default function InicioGeneral() {
  const navigate = useNavigate();
  const [cuerpos, setCuerpos] = useState(() => leerCache());
  const [cargando, setCargando] = useState(() => leerCache().length === 0);
  const [error, setError] = useState("");
  const [logosFallidos, setLogosFallidos] = useState({});

  useEffect(() => {
    document.title = "Bomberos en Servicio | Digital Evolution BM";
    let activo = true;

    async function cargar() {
      const cache = leerCache();
      if (cache.length > 0 && activo) {
        setCuerpos(cache);
        setCargando(false);
      }

      setError("");
      try {
        const q = query(
          collection(db, "cuerpos_bomberos"),
          where("activo", "==", true)
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map(cuerpoSerializable)
          .filter((c) => String(c.slug || "").trim())
          .sort((a, b) => {
            const oa = numeroOrden(a.orden);
            const ob = numeroOrden(b.orden);
            if (oa !== ob) return oa - ob;
            return String(a.nombre || a.id).localeCompare(String(b.nombre || b.id), "es");
          });

        guardarCache(lista);
        if (activo) setCuerpos(lista);
      } catch (e) {
        console.error("Error cargando Cuerpos activos:", e);
        if (activo && cache.length === 0) {
          setError("No se pudieron cargar los Cuerpos de Bomberos disponibles.");
          setCuerpos([]);
        }
      } finally {
        if (activo) setCargando(false);
      }
    }

    cargar();
    return () => { activo = false; };
  }, []);

  const total = useMemo(() => cuerpos.length, [cuerpos]);

  return (
    <main className="inicio-general">
      <section className="inicio-general-hero">
        <div className="inicio-general-marca">Digital Evolution BM</div>
        <h1>Bomberos en Servicio</h1>
        <p>Seleccione su Cuerpo de Bomberos para ingresar a la plataforma.</p>
      </section>

      <section className="inicio-general-contenido">
        {cargando && total === 0 && (
          <div className="inicio-general-estado">Cargando Cuerpos de Bomberos...</div>
        )}
        {error && <div className="inicio-general-error">{error}</div>}

        {!cargando && !error && total === 0 && (
          <div className="inicio-general-estado">
            No hay Cuerpos de Bomberos activos disponibles en este momento.
          </div>
        )}

        {total > 0 && (
          <div className="inicio-general-grid">
            {cuerpos.map((cuerpo) => {
              const logo = logoCuerpo(cuerpo);
              const slug = String(cuerpo.slug || "").trim().toLowerCase();
              const nombre = cuerpo.nombre || `Cuerpo de Bomberos ${cuerpo.id}`;
              const ubicacion = [cuerpo.comuna, cuerpo.region].filter(Boolean).join(" · ");
              const logoOk = logo && !logosFallidos[cuerpo.id];

              return (
                <button
                  type="button"
                  key={cuerpo.id}
                  className="inicio-general-card"
                  onClick={() => navigate(`/${slug}`)}
                >
                  <div className="inicio-general-logo-wrap">
                    {logoOk ? (
                      <img
                        src={logo}
                        alt={`Logo ${nombre}`}
                        className="inicio-general-logo"
                        onError={() => setLogosFallidos((prev) => ({ ...prev, [cuerpo.id]: true }))}
                      />
                    ) : (
                      <div className="inicio-general-logo-placeholder" aria-hidden="true">🚒</div>
                    )}
                  </div>
                  <div className="inicio-general-card-body">
                    <h2>{nombre}</h2>
                    {ubicacion && <p>{ubicacion}</p>}
                    <span>Ingresar</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <footer className="inicio-general-footer">
        <div>Plataforma desarrollada por <strong>Digital Evolution BM</strong></div>
        <div><strong>Max Kendall Blanch Muñoz</strong></div>
        <a href="mailto:contacto@digitalevolution.cl">contacto@digitalevolution.cl</a>
      </footer>
    </main>
  );
}
